import classNames from 'classnames';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import React, { ReactNode, useEffect, useState } from 'react';

import { NewsletterFormInputText } from '../../components/newsletter-form-input';
import { useAuth } from '../../contexts/auth';
import { useCurrentUser } from '../../util/current-user';
import { subscribeToMailchimpList } from '../../util/mailchimp';
import { Submit } from '../submit';
import {
	action as actionStyle,
	error as errorStyle,
	image as imageStyle,
	innerContainer as innerContainerStyle,
	innerContainerText as innerContainerTextStyle,
	inputError as inputErrorStyle,
	outerContainer as outerContainerStyle,
	submitError as submitErrorStyle,
	submitErrorMessage as submitErrorMessageStyle,
} from './styles.module.css';

export interface NewsletterHeroProps {
	title?: string;
	summary?: string;
	renderImage(): ReactNode;
	callToActionTitle?: string;
	callToActionError?: string;
	titleSubmitted?: string;
	summarySubmitted?: string;
	supportEmail?: string;
	errorMessage?: string;
}

interface FormData {
	email?: string;
}

enum FormState {
	NotSubmitted,
	Submitted,
	SubmissionError,
}

export const NewsletterHero = ({
	title,
	summary,
	renderImage,
	callToActionTitle,
	callToActionError,
	titleSubmitted,
	summarySubmitted,
	supportEmail,
	errorMessage,
}: NewsletterHeroProps): JSX.Element => {
	const { session } = useAuth();
	const { user, loading, error, subscribedMailchimpInterests, isMailchimpMember, refetch } =
		useCurrentUser();

	if (!renderImage) throw new Error('Missing renderImage prop to NewsletterHero');

	const [formState, setFormState] = useState<FormState>(FormState.NotSubmitted);

	const handleValidate = (values: FormData) => {
		console.info('validating...');
		const errors: FormData = {};
		if (!values.email) errors.email = 'Please fill in this field';
		if (values.email && values.email.indexOf('@') < 1)
			errors.email = 'Missing an @ symbol for your email address';
		return errors;
	};

	const encode = (data: any) => {
		const searchParams = new URLSearchParams();
		Object.keys(data).map((key) => searchParams.set(key, data[key]));
		return searchParams.toString();
	};

	const handleSubmit = async (values: FormData) => {
		if (!values.email) return;
		try {
			await subscribeToMailchimpList(session, values.email);
			setFormState(FormState.Submitted);
		} catch (error) {
			console.error(error);
			setFormState(FormState.SubmissionError);
		}
	};

	// Add a useEffect such that the email address field is filled in if session is not null and the user's email has been fetched
	const NotSubmittedForm = ({ formErrorMessage }: { formErrorMessage: string | undefined }) => {
		const { setFieldValue } = useFormikContext<FormData>();

		// Pre-fill the email address field if the user is logged in
		useEffect(() => {
			if (session && user?.email) {
				setFieldValue('email', user.email);
			}
		}, [user]);

		return (
			<Form name="newsletter-hero">
				<div
					className={classNames(
						actionStyle,
						formState === FormState.SubmissionError && inputErrorStyle
					)}
				>
					<div>
						<NewsletterFormInputText name="email" type="email" />
						<Submit
							prompt={'Subscribe'}
							supportEmail={supportEmail}
							errorMessage={formErrorMessage}
							submitError={false}
						/>
					</div>
					<ErrorMessage
						name="email"
						render={(message) => <div className={errorStyle}>{message}</div>}
					/>
				</div>
				{formState === FormState.SubmissionError && !!formErrorMessage && !!supportEmail && (
					<div className={submitErrorMessageStyle}>
						{formErrorMessage} <a href={`"mailto:${supportEmail}"`}>{supportEmail}</a>
					</div>
				)}
			</Form>
		);
	};

	const NotSubmitted = ({
		title,
		summary,
		callToAction,
		callToActionError,
		supportEmail,
		formErrorMessage,
	}: {
		title?: string;
		summary?: string;
		callToAction?: string;
		callToActionError?: string;
		supportEmail?: string;
		formErrorMessage?: string;
	}) => (
		<div className={innerContainerStyle}>
			<div className={innerContainerTextStyle}>
				{!!title && <h1>{title}</h1>}
				{!!summary && <p>{summary}</p>}
			</div>
			<Formik
				validate={handleValidate}
				validateOnChange
				validateOnBlur
				initialValues={{ email: '' }}
				onSubmit={handleSubmit}
			>
				<NotSubmittedForm formErrorMessage={formErrorMessage} />
			</Formik>
		</div>
	);

	const Submitted = ({ title, summary }: { title?: string; summary?: string }) => (
		<div className={innerContainerStyle}>
			<div className={innerContainerTextStyle}>
				{!!title && <h1>{title}</h1>}
				{!!summary && <p>{summary}</p>}
			</div>
		</div>
	);

	const subscribedToThisList = isMailchimpMember;

	return (
		<div
			className={classNames(
				outerContainerStyle,
				formState === FormState.SubmissionError && submitErrorStyle
			)}
		>
			<div className={imageStyle}>{renderImage()}</div>
			{formState === FormState.Submitted || subscribedToThisList ? (
				<Submitted title={titleSubmitted} summary={summarySubmitted} />
			) : (
				<NotSubmitted
					title={title}
					summary={summary}
					callToAction={callToActionTitle}
					callToActionError={callToActionError}
					supportEmail={supportEmail}
					formErrorMessage={errorMessage}
				/>
			)}
		</div>
	);
};
